import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert, Box, Button, FormControlLabel, Grid, IconButton, MenuItem, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { Add, ArrowBack, Delete } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import FormFileUpload from '../../components/ui/forms/FormFileUpload';
import {
  getGroceryStores, getGroceryProducts, createGroceryProduct, updateGroceryProduct,
} from '../../services/businessService';
import { getMasters } from '../../services/platform/masterService';
import { matchesParentMaster } from '../../constants/masterRefFields';
import { resolveGroceryThumbnailFromMasters } from '../../utils/groceryMasterImages';
import { chargeFieldLabel } from '../../utils/currency';
import {
  GROCERY_WIZARD_TABS, emptyGroceryWizard, productToWizard, wizardToProductPayload, type GroceryWizardState,
} from './groceryWizardTypes';

type MasterRow = { _id: string; code: string; name: string; parentId?: string | { _id?: string }; metadata?: Record<string, unknown> };

const GROCERY_MASTER_TYPES = [
  'grocery_categories', 'grocery_sub_categories', 'grocery_brands', 'product_variants', 'weight_types',
  'grocery_tax_groups', 'warehouses', 'suppliers', 'stock_alerts', 'grocery_product_images', 'grocery_availability_schedules',
] as const;

const DAYS = [
  { value: 'mon', label: 'Monday' }, { value: 'tue', label: 'Tuesday' }, { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' }, { value: 'fri', label: 'Friday' }, { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
];

async function loadMasters(type: string): Promise<MasterRow[]> {
  const res = await getMasters(type, { limit: 300 });
  return (res.data || []) as MasterRow[];
}

function MasterSelect({
  label, value, onChange, options, required,
}: {
  label: string; value: string; onChange: (v: string) => void; options: MasterRow[]; required?: boolean;
}) {
  return (
    <TextField select fullWidth size="small" label={label} value={value} onChange={(e) => onChange(e.target.value)} required={required}>
      <MenuItem value="">— Select —</MenuItem>
      {options.map((o) => <MenuItem key={o._id} value={o.code}>{o.name}</MenuItem>)}
    </TextField>
  );
}

export default function GroceryAddWizardPage() {
  const navigate = useNavigate();
  const { productId } = useParams<{ productId?: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!productId;
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState<GroceryWizardState>(() => emptyGroceryWizard(searchParams.get('storeId') || ''));
  const patch = (partial: Partial<GroceryWizardState>) => setForm((f) => ({ ...f, ...partial }));

  const { data: stores = [] } = useQuery({ queryKey: ['grocery-stores-wizard'], queryFn: () => getGroceryStores(true) });
  const storeId = form.storeId || stores[0]?._id;

  const { data: products = [] } = useQuery({
    queryKey: ['grocery-products-wizard', storeId],
    queryFn: () => getGroceryProducts(storeId),
    enabled: !!storeId,
  });

  useEffect(() => {
    if (!isEdit || !productId || !products.length) return;
    const item = products.find((p: { _id: string }) => p._id === productId);
    if (item) setForm(productToWizard(item as Record<string, unknown>, storeId));
  }, [isEdit, productId, products, storeId]);

  const mastersQuery = useQuery({
    queryKey: ['grocery-add-masters-full'],
    queryFn: async () => {
      const entries = await Promise.all(GROCERY_MASTER_TYPES.map(async (t) => [t, await loadMasters(t)] as const));
      return Object.fromEntries(entries) as Record<string, MasterRow[]>;
    },
  });

  const m = mastersQuery.data || {};

  useEffect(() => {
    if (isEdit || !mastersQuery.data || form.image) return;
    const sku = form.sku || form.name.slice(0, 12).toUpperCase().replace(/\s/g, '_');
    const url = resolveGroceryThumbnailFromMasters({
      skuCode: sku,
      categoryCode: form.categoryCode,
      brandCode: form.brandCode,
      productImages: m.grocery_product_images || [],
      categories: m.grocery_categories || [],
      brands: m.grocery_brands || [],
    });
    if (url) patch({ image: url });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.sku, form.name, form.categoryCode, form.brandCode, mastersQuery.data, isEdit]);

  const subCategories = useMemo(() => {
    const rows = m.grocery_sub_categories || [];
    if (!form.categoryCode) return rows;
    const cat = (m.grocery_categories || []).find((c) => c.code === form.categoryCode);
    return rows.filter((r) => matchesParentMaster(r, form.categoryCode, cat ? { _id: cat._id } : undefined, 'parentCategoryCode'));
  }, [m.grocery_sub_categories, m.grocery_categories, form.categoryCode]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = wizardToProductPayload({ ...form, storeId });
      if (isEdit && productId) return updateGroceryProduct(productId, payload);
      return createGroceryProduct(storeId, payload);
    },
    onSuccess: () => navigate(`/grocery/products?storeId=${storeId}`),
  });

  const canSave = !!form.name && !!storeId && form.price >= 0;

  const loadImageFromMasters = () => {
    const sku = form.sku || form.name.slice(0, 12).toUpperCase().replace(/\s/g, '_');
    const url = resolveGroceryThumbnailFromMasters({
      skuCode: sku,
      categoryCode: form.categoryCode,
      brandCode: form.brandCode,
      productImages: m.grocery_product_images || [],
      categories: m.grocery_categories || [],
      brands: m.grocery_brands || [],
    });
    if (url) patch({ image: url });
    const gallery = (m.grocery_product_images || [])
      .filter((row) => String(row.metadata?.skuCode || '').toUpperCase() === sku.toUpperCase() && row.metadata?.imageType === 'gallery')
      .map((row) => String(row.metadata?.imageUrl || ''))
      .filter(Boolean);
    if (gallery.length) patch({ galleryImages: gallery });
  };

  const applyScheduleFromMasters = () => {
    const schedule = (m.grocery_availability_schedules || []).map((row) => ({
      day: String(row.metadata?.day || 'mon'),
      startTime: String(row.metadata?.startTime || '07:00'),
      endTime: String(row.metadata?.endTime || '22:00'),
    }));
    if (schedule.length) patch({ scheduleAvailability: true, availabilitySchedule: schedule });
  };

  return (
    <PageShell>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/grocery/products?storeId=${storeId}`)}>Back to Products</Button>
      </Stack>
      <PageHeader
        title={isEdit ? 'Edit Grocery Product' : 'Add Grocery Product'}
        subtitle="6-tab wizard — all 12 grocery masters drive category, supply, availability, and images."
      />

      {!mastersQuery.isLoading && !mastersQuery.data?.grocery_categories?.length && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Complete grocery masters first under Delivery → Grocery Delivery → Masters.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {GROCERY_WIZARD_TABS.map((label) => <Tab key={label} label={label} />)}
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField select fullWidth size="small" label="Store" value={storeId} onChange={(e) => patch({ storeId: e.target.value })}>
              {stores.map((s: { _id: string; name: string }) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}><TextField fullWidth size="small" label="Product Name" value={form.name} onChange={(e) => patch({ name: e.target.value })} required /></Grid>
          <Grid item xs={12} md={6}><TextField fullWidth size="small" label="SKU" value={form.sku} onChange={(e) => patch({ sku: e.target.value })} helperText="Used for product_images master lookup" /></Grid>
          <Grid item xs={12} md={6}><MasterSelect label="Category *" value={form.categoryCode} onChange={(v) => patch({ categoryCode: v, subCategoryCode: '' })} options={m.grocery_categories || []} required /></Grid>
          <Grid item xs={12} md={6}><MasterSelect label="Sub Category" value={form.subCategoryCode} onChange={(v) => patch({ subCategoryCode: v })} options={subCategories} /></Grid>
          <Grid item xs={12} md={6}><MasterSelect label="Brand" value={form.brandCode} onChange={(v) => patch({ brandCode: v })} options={m.grocery_brands || []} /></Grid>
          <Grid item xs={12} md={6}><MasterSelect label="Variant" value={form.variantCode} onChange={(v) => patch({ variantCode: v })} options={m.product_variants || []} /></Grid>
          <Grid item xs={12} md={6}><MasterSelect label="Weight Type" value={form.weightTypeCode} onChange={(v) => patch({ weightTypeCode: v })} options={m.weight_types || []} /></Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}><TextField fullWidth size="small" type="number" label={chargeFieldLabel('Price')} value={form.price} onChange={(e) => patch({ price: Number(e.target.value) })} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth size="small" type="number" label={chargeFieldLabel('Offer Price')} value={form.discountedPrice} onChange={(e) => patch({ discountedPrice: Number(e.target.value) })} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth size="small" type="number" label="Stock" value={form.stock} onChange={(e) => patch({ stock: Number(e.target.value) })} /></Grid>
          <Grid item xs={12} md={4}><TextField fullWidth size="small" label="Unit" value={form.unit} onChange={(e) => patch({ unit: e.target.value })} /></Grid>
          <Grid item xs={12} md={4}><MasterSelect label="Tax Group" value={form.taxGroupCode} onChange={(v) => patch({ taxGroupCode: v })} options={m.grocery_tax_groups || []} /></Grid>
        </Grid>
      )}

      {tab === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}><MasterSelect label="Warehouse" value={form.warehouseCode} onChange={(v) => patch({ warehouseCode: v })} options={m.warehouses || []} /></Grid>
          <Grid item xs={12} md={6}><MasterSelect label="Supplier" value={form.supplierCode} onChange={(v) => patch({ supplierCode: v })} options={m.suppliers || []} /></Grid>
          <Grid item xs={12} md={6}><MasterSelect label="Stock Alert Threshold" value={form.stockAlertCode} onChange={(v) => patch({ stockAlertCode: v })} options={m.stock_alerts || []} /></Grid>
        </Grid>
      )}

      {tab === 3 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <FormControlLabel control={<Switch checked={form.scheduleAvailability} onChange={(e) => patch({ scheduleAvailability: e.target.checked })} />} label="Use availability schedule" />
              <Button size="small" variant="outlined" onClick={applyScheduleFromMasters}>Load from masters</Button>
            </Stack>
          </Grid>
          {form.scheduleAvailability && form.availabilitySchedule.map((row, i) => (
            <Grid container spacing={2} key={i} item xs={12}>
              <Grid item xs={12} sm={3}>
                <TextField select fullWidth size="small" label="Day" value={row.day} onChange={(e) => {
                  const availabilitySchedule = [...form.availabilitySchedule];
                  availabilitySchedule[i] = { ...row, day: e.target.value };
                  patch({ availabilitySchedule });
                }}>
                  {DAYS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={5} sm={3}><TextField fullWidth size="small" label="Start" value={row.startTime} onChange={(e) => {
                const availabilitySchedule = [...form.availabilitySchedule];
                availabilitySchedule[i] = { ...row, startTime: e.target.value };
                patch({ availabilitySchedule });
              }} /></Grid>
              <Grid item xs={5} sm={3}><TextField fullWidth size="small" label="End" value={row.endTime} onChange={(e) => {
                const availabilitySchedule = [...form.availabilitySchedule];
                availabilitySchedule[i] = { ...row, endTime: e.target.value };
                patch({ availabilitySchedule });
              }} /></Grid>
              <Grid item xs={2} sm={1} display="flex" alignItems="center">
                <IconButton color="error" onClick={() => patch({ availabilitySchedule: form.availabilitySchedule.filter((_, j) => j !== i) })}><Delete /></IconButton>
              </Grid>
            </Grid>
          ))}
          {form.scheduleAvailability && (
            <Grid item xs={12}>
              <Button startIcon={<Add />} onClick={() => patch({ availabilitySchedule: [...form.availabilitySchedule, { day: 'mon', startTime: '07:00', endTime: '22:00' }] })}>Add Slot</Button>
            </Grid>
          )}
        </Grid>
      )}

      {tab === 4 && (
        <Box>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button size="small" variant="outlined" onClick={loadImageFromMasters}>Load from masters</Button>
          </Stack>
          <FormFileUpload label="Product Image" value={form.image} onChange={(url) => patch({ image: url })} folder="grocery/products" required />
          {form.galleryImages.map((url, i) => (
            <Stack key={i} direction="row" spacing={1} sx={{ mt: 1 }} alignItems="flex-start">
              <Box flex={1}><FormFileUpload label={`Gallery ${i + 1}`} value={url} onChange={(nextUrl) => {
                const galleryImages = [...form.galleryImages];
                galleryImages[i] = nextUrl;
                patch({ galleryImages });
              }} folder="grocery/products/gallery" /></Box>
              <IconButton onClick={() => patch({ galleryImages: form.galleryImages.filter((_, j) => j !== i) })}><Delete /></IconButton>
            </Stack>
          ))}
          <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => patch({ galleryImages: [...form.galleryImages, ''] })}>Add Gallery Image</Button>
        </Box>
      )}

      {tab === 5 && (
        <Grid container spacing={2}>
          <Grid item xs={12}><TextField fullWidth size="small" multiline rows={3} label="Description" value={form.description} onChange={(e) => patch({ description: e.target.value })} /></Grid>
          <Grid item xs={12}><FormControlLabel control={<Switch checked={form.allowSubstitute} onChange={(e) => patch({ allowSubstitute: e.target.checked })} />} label="Allow substitution if out of stock" /></Grid>
          <Grid item xs={12}>
            <TextField select fullWidth size="small" label="Status" value={form.isActive ? 'active' : 'inactive'} onChange={(e) => patch({ isActive: e.target.value === 'active' })}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      )}

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
        <Button disabled={tab === 0} onClick={() => setTab((t) => t - 1)}>Previous</Button>
        <Stack direction="row" spacing={1}>
          {tab < GROCERY_WIZARD_TABS.length - 1 ? (
            <Button variant="contained" onClick={() => setTab((t) => t + 1)}>Next</Button>
          ) : (
            <Button variant="contained" disabled={!canSave || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
              {saveMutation.isPending ? 'Saving…' : isEdit ? 'Update Product' : 'Create Product'}
            </Button>
          )}
        </Stack>
      </Stack>
    </PageShell>
  );
}
